import { useEffect, useRef } from 'react';

/**
 * Polls `fn` on an interval until it resolves `true` or consecutive failures
 * reach `maxFailures`, then calls `onDone`.
 * @param root0 options
 */
export function usePoll({
  enabled,
  fn,
  onDone,
  initialDelayMs = 15_000,
  pollIntervalMs = 5_000,
  maxFailures = 3,
}: {
  enabled: boolean;
  fn: () => Promise<boolean>;
  onDone: () => void;
  initialDelayMs?: number;
  pollIntervalMs?: number;
  maxFailures?: number;
}): void {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    if (!enabled) return undefined;

    let failureCount = 0;
    let stopped = false;
    let interval: ReturnType<typeof setInterval>;

    const stop = () => {
      stopped = true;
      clearInterval(interval);
      onDoneRef.current();
    };

    const tick = async () => {
      if (stopped) return;
      try {
        const done = await fnRef.current();
        failureCount = 0;
        if (done) stop();
      } catch {
        if (++failureCount >= maxFailures) stop();
      }
    };

    const timeout = setTimeout(() => {
      interval = setInterval(tick, pollIntervalMs);
    }, initialDelayMs);

    return () => {
      stopped = true;
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, [enabled, initialDelayMs, pollIntervalMs, maxFailures]);
}
